import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {ArrowLeft} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import PhoneInput, { type PaisOpcao, PAISES, validarNumero } from '@/components/PhoneInput'
import {
  CONDICOES_SAUDE,
  PERGUNTAS,
  OPCOES_EXPERIENCIA,
  OPCOES_PRONTIDAO,
  OPCOES_OBSTACULOS,
  OLEOS_INFO,
} from '@/types/diagnostico'
import { calcularOleosIndicados } from '@/utils/oleosLogic'
import { filtrarPrioridadesVisiveis } from '@/utils/prioridades'
import BlocoApresentacaoConsultora from '@/components/BlocoApresentacaoConsultora'

// ---------------------------------------------------------------------------
// Utilitários
// ---------------------------------------------------------------------------
function gerarSlugDe(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

type Step = 'intro' | 'lead' | 'condicoes' | 'perguntas' | 'perfil' | 'resultado'

const LABELS_CARDS_FISICOS: Record<string, string> = {
  sono: 'Dificuldade para dormir',
  disposicao: 'Cansaço e falta de energia',
  foco: 'Distração e dispersão mental',
  memoria: 'Esquecimento e falta de clareza',
  ansiedade: 'Ansiedade e tensão no corpo',
  peso: 'Oscilação de peso e alimentação emocional',
  dores: 'Dores de cabeça, no corpo e tensão muscular',
  medicamentos: 'Uso recorrente de medicamentos',
  inflamacoes: 'Baixa imunidade ou quadros inflamatórios',
  intestino: 'Desconfortos digestivos',
}

const LABELS_CARDS_EMOCIONAIS: Record<string, string> = {
  vergonha: 'Medo do julgamento',
  culpa: 'Excesso de responsabilidade',
  apatia: 'Desânimo e indiferença',
  tristeza: 'Tristeza frequente',
  medo: 'Medo de mudanças',
  escassez: 'Sentimento de escassez',
  raiva: 'Irritabilidade fácil',
  orgulho: 'Dificuldade de pedir ajuda',
  estagnacao: 'Ciclos que se repetem',
  desequilibrio: 'Desequilíbrio emocional',
}

function DiagnosticoCorporativo() {
  // Parâmetros de URL
  const params = new URLSearchParams(window.location.search)
  const orgSlug = params.get('org') || ''
  const consultorParam = params.get('c') || 'monica'

  const [step, setStep] = useState<Step>('intro')
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [currentPerfil, setCurrentPerfil] = useState(0)
  const [leadId, setLeadId] = useState<string | null>(null)
  const [empresa, setEmpresa] = useState<{ nome: string; slug: string } | null>(null)
  const [carregandoEmpresa, setCarregandoEmpresa] = useState(true)

  // Form
  const [nome, setNome] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')
  const [idade, setIdade] = useState('')
  const [pais, setPais] = useState('Brasil')
  const [paisObj, setPaisObj] = useState<PaisOpcao>(PAISES[0])
  const [condicoes, setCondicoes] = useState<string[]>([])
  const [respostas, setRespostas] = useState<Record<string, boolean>>({})
  const [experiencia, setExperiencia] = useState('')
  const [prontidao, setProntidao] = useState('')
  const [obstaculo, setObstaculo] = useState('')
  const [prioridades, setPrioridades] = useState<string[]>([])
  const [avisoMaxPrioridades, setAvisoMaxPrioridades] = useState(false)
  const [oleosIndicados, setOleosIndicados] = useState<string[]>([])
  const [alertas, setAlertas] = useState<string[]>([])
  const [exibirOleosResultadoCorporativo, setExibirOleosResultadoCorporativo] = useState(false)

  const consultora = consultorParam.toLowerCase().trim()

  const deveExibirOleos = (valor: unknown): boolean =>
    valor === true || valor === 'true' || valor === 1 || valor === '1'

  // Carregar dados da empresa pelo slug
  useEffect(() => {
    if (!orgSlug) { setCarregandoEmpresa(false); return }
    ;(async () => {
      try {
        const res = await lumi.entities.empresas.list({ filter: { slug: orgSlug }, limit: 1 })
        const emp = (res?.list ?? [])[0]
        if (emp) setEmpresa({ nome: emp.nome, slug: emp.slug })
      } catch (e) {
        console.error('[corporativo] erro ao carregar empresa:', e)
      } finally {
        setCarregandoEmpresa(false)
      }
    })()
  }, [orgSlug])

  useEffect(() => {
    const slug = (consultora || '').toLowerCase().trim()
    if (!slug || slug === 'geral') {
      setExibirOleosResultadoCorporativo(false)
      return
    }
    let ativo = true
    void (async () => {
      try {
        const resp = await lumi.entities.consultoras.list({
          filter: { usuario: slug },
          limit: 1,
        })
        if (!ativo) return
        setExibirOleosResultadoCorporativo(deveExibirOleos(resp?.list?.[0]?.exibirOleosResultadoCorporativo))
      } catch (err) {
        console.error('[corporativo] erro ao carregar configuracao da consultora', err)
        if (ativo) setExibirOleosResultadoCorporativo(false)
      }
    })()
    return () => {
      ativo = false
    }
  }, [consultora])

  const totalSteps = 25 // lead + condicoes + 20 perguntas + 3 perfil
  const currentStepNumber =
    step === 'intro' ? 0
    : step === 'lead' ? 1
    : step === 'condicoes' ? 2
    : step === 'perguntas' ? 3 + currentQuestion
    : step === 'perfil' ? 23 + currentPerfil
    : 25
  const progress = (currentStepNumber / totalSteps) * 100

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  }

  const salvarLeadInicial = async () => {
    try {
      const now = new Date().toISOString()
      const digitos = whatsapp.replace(/\D/g, '')
      const whatsappFormatado = paisObj.codigo ? `${paisObj.codigo}${digitos}` : digitos
      const lead = await lumi.entities.leads2.create({
        nome,
        whatsapp: whatsappFormatado,
        email,
        idade: idade ? Number(idade) : null,
        pais,
        data_hora: now,
        status: 'Novo lead',
        condicoes_saude: [],
        respostas: {},
        oleos_indicados: [],
        experiencia_oleos: '',
        prontidao: '',
        obstaculo: '',
        objetivo_saude: '',
        prioridades: [],
        interesses: [],
        clicou_whatsapp: 'Não',
        compartilhou: 'Não',
        consultora: consultora || 'geral',
        empresa: orgSlug || '',
        empresaNome: empresa?.nome || '',
        creator: 'system',
        createdAt: now,
        updatedAt: now,
      })
      setLeadId(lead._id)
      toast.success('Dados salvos!')
    } catch (err) {
      console.error('[corporativo] erro ao salvar lead:', err)
      toast.error('Erro ao salvar dados')
    }
  }

  const atualizarLead = async (dados: Partial<any>) => {
    if (!leadId) return
    try {
      await lumi.entities.leads2.update(leadId, { ...dados, updatedAt: new Date().toISOString() })
    } catch (err) {
      console.error('[corporativo] erro ao atualizar lead:', err)
    }
  }

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validacao = validarNumero(whatsapp, paisObj)
    if (!validacao.valido) { toast.error(validacao.mensagem); return }
    if (!idade || isNaN(Number(idade)) || Number(idade) <= 0) { toast.error('Por favor informe sua idade'); return }
    await salvarLeadInicial()
    setStep('condicoes')
  }

  const handleCondicoesSubmit = async () => {
    await atualizarLead({ condicoes_saude: condicoes })
    setStep('perguntas')
  }

  const handlePerguntaResposta = async (resposta: boolean) => {
    const perguntaId = PERGUNTAS[currentQuestion].id
    const novasRespostas = { ...respostas, [perguntaId]: resposta }
    setRespostas(novasRespostas)
    setTimeout(() => {
      if (currentQuestion < PERGUNTAS.length - 1) {
        setCurrentQuestion(currentQuestion + 1)
      } else {
        atualizarLead({ respostas: novasRespostas })
        setStep('perfil')
      }
    }, 400)
  }

  const handlePerfilNext = async () => {
    if (currentPerfil === 0 && !experiencia) { toast.error('Selecione uma opção'); return }
    if (currentPerfil === 1 && !prontidao) { toast.error('Selecione uma opção'); return }
    if (currentPerfil === 2 && !obstaculo) { toast.error('Selecione uma opção'); return }
    if (currentPerfil === 3 && prioridades.length === 0) { toast.error('Selecione pelo menos 1 prioridade'); return }

    if (currentPerfil < 3) {
      setCurrentPerfil(currentPerfil + 1)
    } else {
      const resultado = calcularOleosIndicados(respostas, condicoes)
      setOleosIndicados(resultado.oleos)
      setAlertas(resultado.alertas)
      await atualizarLead({
        experiencia_oleos: experiencia,
        prontidao,
        obstaculo,
        interesses: [],
        objetivo_saude: '',
        prioridades,
        oleos_indicados: resultado.oleos,
      })
      setStep('resultado')
    }
  }

  const handleVoltar = () => {
    if (step === 'lead') setStep('intro')
    else if (step === 'condicoes') setStep('lead')
    else if (step === 'perguntas' && currentQuestion > 0) setCurrentQuestion(currentQuestion - 1)
    else if (step === 'perguntas' && currentQuestion === 0) setStep('condicoes')
    else if (step === 'perfil' && currentPerfil > 0) setCurrentPerfil(currentPerfil - 1)
    else if (step === 'perfil' && currentPerfil === 0) { setCurrentQuestion(PERGUNTAS.length - 1); setStep('perguntas') }
  }

  if (carregandoEmpresa) {
    return (
      <div className="min-h-screen bg-bege-fundo flex items-center justify-center">
        <div className="text-texto-suave">Carregando...</div>
      </div>
    )
  }

  if (!orgSlug || !empresa) {
    return (
      <div className="min-h-screen bg-bege-fundo flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-md">
          <div className="text-4xl mb-4">🔗</div>
          <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-3">Link inválido</h2>
          <p className="text-texto-suave text-sm">O link que você acessou não corresponde a nenhuma empresa cadastrada. Por favor, utilize o link correto fornecido pela sua empresa.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bege-fundo">
      {/* Barra de progresso */}
      {step !== 'resultado' && step !== 'intro' && (
        <div className="fixed top-0 left-0 w-full bg-white shadow-sm z-50">
          <div className="h-2 bg-bege-medio">
            <motion.div
              className="h-full bg-verde-escuro"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="container mx-auto px-4 py-3 flex justify-between items-center">
            {step !== 'lead' && (
              <button
                onClick={handleVoltar}
                className="flex items-center gap-2 text-texto-suave hover:text-texto-escuro transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Voltar
              </button>
            )}
            <div className="ml-auto text-sm text-texto-suave">{Math.round(progress)}%</div>
          </div>
        </div>
      )}

      <div className={step === 'intro' ? 'pt-8 pb-12 px-4' : 'pt-20 pb-12 px-4'}>
        <div className="container mx-auto max-w-3xl">
          <AnimatePresence mode="wait">

            {/* ── INTRO ── */}
            {step === 'intro' && (
              <motion.div
                key="intro"
                variants={pageVariants}
                initial="initial" animate="animate" exit="exit"
                transition={{ duration: 0.4 }}
              >
                <BlocoApresentacaoConsultora
                  consultora={consultora}
                  modo="intro"
                  mensagemFixa="Que bom ter você aqui! Este diagnóstico foi disponibilizado pela sua empresa como parte de uma iniciativa de cuidado com a saúde e bem-estar da equipe. Ao final você receberá um resultado individual e entrarei em contato para te orientar, esclarecer suas dúvidas e te ajudar a cuidar mais de você."
                />

                <div className="text-center">
                  <span className="inline-block bg-[#D8F3DC] text-[#2D6A4F] text-sm font-medium px-4 py-1.5 rounded-full mb-4">
                    Diagnóstico de Saúde e Emoções
                  </span>
                  <h1 className="text-4xl font-cormorant font-semibold text-texto-escuro mb-3">
                    Descubra o que está afetando sua energia, emoções e bem-estar
                  </h1>
                  <p className="text-texto-suave mb-6 leading-relaxed max-w-xl mx-auto">
                    Receba uma orientação para seus desconfortos e saiba como equilibrar sua saúde de forma natural.<br /><br />
                    Responda com sinceridade. Cada pergunta revela um caminho.
                  </p>
                  <div className="max-w-sm mx-auto space-y-3 text-left mb-8">
                    {[
                      '20 perguntas sobre saúde física e emocional',
                      'Resultado personalizado ao final',
                      'Leva cerca de 5 minutos',
                      'Suas respostas são 100% confidenciais',
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full bg-[#D8F3DC] flex items-center justify-center flex-shrink-0">
                          <svg className="w-3.5 h-3.5 text-[#2D6A4F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-texto-suave text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setStep('lead')}
                    className="w-full max-w-sm mx-auto block bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all text-lg"
                  >
                    Começar meu diagnóstico →
                  </button>
                  <p className="text-sm text-texto-suave mt-4">
                    🔒 Seus dados são confidenciais.
                  </p>
                </div>
              </motion.div>
            )}

            {/* ── LEAD ── */}
            {step === 'lead' && (
              <motion.div key="lead" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.4 }}>
                <p className="text-marrom-suave italic mb-4 text-center">Antes de começar</p>
                <h2 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-4 text-center">Me conta um pouco sobre você</h2>
                <p className="text-texto-suave text-center mb-8">Seus dados são usados apenas para enviar seu protocolo personalizado.</p>
                <form onSubmit={handleLeadSubmit} className="space-y-6">
                  <div>
                    <label className="block text-texto-escuro mb-2">Nome completo</label>
                    <input type="text" value={nome} onChange={e => setNome(e.target.value)} required
                      className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-texto-escuro mb-2">WhatsApp</label>
                    <PhoneInput value={whatsapp} onChange={(n, p) => { setWhatsapp(n); setPaisObj(p); setPais(p.nomeSalvo) }} required />
                  </div>
                  <div>
                    <label className="block text-texto-escuro mb-2">Sua idade</label>
                    <input type="number" value={idade} onChange={e => setIdade(e.target.value)} required inputMode="numeric" placeholder="Ex: 35"
                      className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-texto-escuro mb-2">E-mail</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                      className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none" />
                  </div>
                  <button type="submit"
                    disabled={!validarNumero(whatsapp, paisObj).valido || !idade || Number(idade) <= 0}
                    className="w-full bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                    Continuar →
                  </button>
                  <p className="text-sm text-texto-suave text-center">🔒 Seus dados são confidenciais e não serão compartilhados.</p>
                </form>
              </motion.div>
            )}

            {/* ── CONDIÇÕES ── */}
            {step === 'condicoes' && (
              <motion.div key="condicoes" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.4 }}>
                <div className="text-center mb-8">
                  <h1 className="text-4xl font-cormorant font-semibold text-texto-escuro mb-4">Você tem alguma condição de saúde?</h1>
                  <p className="text-texto-suave">Pode marcar mais de uma.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {CONDICOES_SAUDE.map(condicao => (
                    <label key={condicao}
                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${condicoes.includes(condicao) ? 'border-verde-claro bg-verde-muito-claro' : 'border-bege-medio hover:border-verde-claro'}`}>
                      <input type="checkbox" checked={condicoes.includes(condicao)}
                        onChange={e => {
                          if (condicao === 'Nenhuma') { setCondicoes(e.target.checked ? ['Nenhuma'] : []) }
                          else { const f = condicoes.filter(c => c !== 'Nenhuma'); setCondicoes(e.target.checked ? [...f, condicao] : f.filter(c => c !== condicao)) }
                        }} className="w-5 h-5 text-verde-escuro" />
                      <span className="text-texto-escuro">{condicao}</span>
                    </label>
                  ))}
                </div>
                <button onClick={handleCondicoesSubmit}
                  className="w-full bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all">
                  Continuar →
                </button>
              </motion.div>
            )}

            {/* ── PERGUNTAS ── */}
            {step === 'perguntas' && (
              <motion.div key={`pergunta-${currentQuestion}`} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.4 }}>
                <div className="text-center mb-12">
                  <h1 className="text-3xl md:text-4xl font-cormorant font-semibold text-texto-escuro mb-8">
                    {PERGUNTAS[currentQuestion].texto}
                  </h1>
                  <div className="flex gap-4 justify-center">
                    <button onClick={() => handlePerguntaResposta(true)}
                      className="px-12 py-4 bg-white border-2 border-bege-medio text-texto-escuro rounded-full font-medium hover:bg-verde-escuro hover:text-white hover:border-verde-escuro transition-all text-lg">
                      Sim
                    </button>
                    <button onClick={() => handlePerguntaResposta(false)}
                      className="px-12 py-4 bg-white border-2 border-bege-medio text-texto-escuro rounded-full font-medium hover:bg-verde-escuro hover:text-white hover:border-verde-escuro transition-all text-lg">
                      Não
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── PERFIL (sem pergunta de interesses) ── */}
            {step === 'perfil' && (
              <motion.div key={`perfil-${currentPerfil}`} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.4 }}>
                {currentPerfil === 0 && (
                  <div>
                    <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-8 text-center">
                      Se você já experimentou óleos essenciais, como foi sua experiência?
                    </h1>
                    <div className="space-y-3">
                      {OPCOES_EXPERIENCIA.map(opcao => (
                        <label key={opcao}
                          className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${experiencia === opcao ? 'border-verde-claro bg-verde-muito-claro' : 'border-bege-medio hover:border-verde-claro'}`}>
                          <input type="radio" name="experiencia" checked={experiencia === opcao} onChange={() => setExperiencia(opcao)} className="mr-3" />
                          {opcao}
                        </label>
                      ))}
                    </div>
                    <button onClick={handlePerfilNext}
                      className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all">
                      Continuar →
                    </button>
                  </div>
                )}

                {currentPerfil === 1 && (
                  <div>
                    <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-8 text-center">
                      Sabendo que existe um protocolo personalizado para os seus desconfortos, como você se descreveria agora?
                    </h1>
                    <div className="space-y-3">
                      {OPCOES_PRONTIDAO.map(opcao => (
                        <label key={opcao}
                          className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${prontidao === opcao ? 'border-verde-claro bg-verde-muito-claro' : 'border-bege-medio hover:border-verde-claro'}`}>
                          <input type="radio" name="prontidao" checked={prontidao === opcao} onChange={() => setProntidao(opcao)} className="mr-3" />
                          {opcao}
                        </label>
                      ))}
                    </div>
                    <button onClick={handlePerfilNext}
                      className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all">
                      Continuar →
                    </button>
                  </div>
                )}

                {currentPerfil === 2 && (
                  <div>
                    <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-8 text-center">
                      Qual é o maior obstáculo que você enfrenta quando pensa em cuidar da sua saúde de forma mais ativa?
                    </h1>
                    <div className="space-y-3">
                      {OPCOES_OBSTACULOS.map(opcao => (
                        <label key={opcao}
                          className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${obstaculo === opcao ? 'border-verde-claro bg-verde-muito-claro' : 'border-bege-medio hover:border-verde-claro'}`}>
                          <input type="radio" name="obstaculo" checked={obstaculo === opcao} onChange={() => setObstaculo(opcao)} className="mr-3" />
                          {opcao}
                        </label>
                      ))}
                    </div>
                    <button onClick={handlePerfilNext}
                      className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all">
                      Continuar →
                    </button>
                  </div>
                )}

                {currentPerfil === 3 && (() => {
                  const gruposVisiveis = filtrarPrioridadesVisiveis(respostas, condicoes)
                  return (
                    <div>
                      <p className="text-marrom-suave italic mb-4 text-center">Última pergunta</p>
                      <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-4 text-center">
                        Para finalizar: o que você mais quer resolver ou melhorar na sua saúde hoje?
                      </h1>
                      <p className="text-texto-suave mb-2 text-center">Selecione até 3 prioridades.</p>
                      <p className="text-sm text-texto-suave mb-8 text-center">{prioridades.length}/3 selecionada(s)</p>
                      {avisoMaxPrioridades && (
                        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6 rounded">
                          <p className="text-orange-800 text-sm">Você já selecionou 3 prioridades. Desmarque uma para selecionar outra.</p>
                        </div>
                      )}
                      {gruposVisiveis.length === 0 ? (
                        <div className="bg-verde-muito-claro border-l-4 border-verde-escuro p-6 rounded mb-6">
                          <p className="text-texto-escuro">Com base nas suas respostas, não identificamos prioridades específicas para destacar. Você pode prosseguir para ver seu resultado.</p>
                        </div>
                      ) : (
                        <div className="space-y-8">
                          {gruposVisiveis.map(grupo => (
                            <div key={grupo.titulo}>
                              <h3 className="text-xl font-cormorant font-semibold text-verde-escuro mb-4">{grupo.titulo}</h3>
                              <div className="space-y-3">
                                {grupo.opcoes.map(opcao => {
                                  const sel = prioridades.includes(opcao.id)
                                  return (
                                    <label key={opcao.id}
                                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${sel ? 'border-verde-claro bg-verde-muito-claro' : 'border-bege-medio hover:border-verde-claro'}`}>
                                      <input type="checkbox" checked={sel}
                                        onChange={e => {
                                          if (e.target.checked) {
                                            if (prioridades.length >= 3) { setAvisoMaxPrioridades(true); setTimeout(() => setAvisoMaxPrioridades(false), 3500); return }
                                            setPrioridades([...prioridades, opcao.id]); setAvisoMaxPrioridades(false)
                                          } else { setPrioridades(prioridades.filter(p => p !== opcao.id)); setAvisoMaxPrioridades(false) }
                                        }} className="w-5 h-5 text-verde-escuro" />
                                      <span className="text-texto-escuro">{opcao.label}</span>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <button onClick={handlePerfilNext}
                        className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all">
                        Ver meu resultado →
                      </button>
                    </div>
                  )
                })()}
              </motion.div>
            )}

            {/* ── RESULTADO ── */}
            {step === 'resultado' && (() => {
              const condicoesRelevantes = condicoes.filter(c => c !== 'Nenhuma')
              const cardsFisicos = Object.keys(LABELS_CARDS_FISICOS).filter(id => respostas[id])
              const cardsEmocionais = Object.keys(LABELS_CARDS_EMOCIONAIS).filter(id => respostas[id])
              return (
              <motion.div key="resultado" variants={pageVariants} initial="initial" animate="animate" transition={{ duration: 0.4 }}>
                <div className="bg-verde-escuro text-white px-6 py-12 rounded-2xl mb-8 text-center">
                  <h1 className="text-4xl font-cormorant font-semibold mb-4">
                    Isso é o que seu corpo e suas emoções estão revelando, {nome.split(' ')[0]} 🌿
                  </h1>
                  <p className="text-lg">
                    De acordo com tudo que você respondeu, confira como está sua saúde física e emocional.
                  </p>
                </div>

                {alertas.map((alerta, i) => (
                  <div key={i} className="bg-orange-100 border-l-4 border-orange-500 p-4 mb-6 rounded">
                    <p className="text-orange-800">{alerta}</p>
                  </div>
                ))}

                {condicoesRelevantes.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                    <h3 className="font-cormorant text-2xl font-semibold text-texto-escuro mb-4">
                      Com base no que você nos informou, você já convive com:
                    </h3>
                    <ul className="list-disc list-inside space-y-1 mb-4">
                      {condicoesRelevantes.map(c => (
                        <li key={c} className="text-texto-suave">{c}</li>
                      ))}
                    </ul>
                    <p className="text-sm text-texto-suave italic">
                      Esses fatores foram considerados na orientação dos seus óleos essenciais.
                    </p>
                  </div>
                )}

                {(() => {
                  const idsFisicos = Object.keys(LABELS_CARDS_FISICOS)
                  const idsEmocionais = Object.keys(LABELS_CARDS_EMOCIONAIS)

                  const calcularIndicador = (ids: string[]) => {
                    const total = ids.length
                    const simCount = ids.filter(id => respostas[id]).length
                    const percentual = total > 0 ? Math.round((simCount / total) * 100) : 0
                    return { simCount, total, percentual }
                  }

                  const fisico = calcularIndicador(idsFisicos)
                  const emocional = calcularIndicador(idsEmocionais)

                  const nivelTexto = (percentual: number) => {
                    if (percentual >= 61) return 'Atenção necessária'
                    if (percentual >= 31) return 'Pontos a observar'
                    return 'Bom equilíbrio'
                  }

                  const corBarra = (percentual: number) => {
                    if (percentual >= 61) return '#EF4444'
                    if (percentual >= 31) return '#F59E0B'
                    return '#52B788'
                  }

                  return (
                    <>
                      {cardsFisicos.length > 0 && (
                        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-cormorant text-2xl font-semibold text-texto-escuro">Saúde Física</h3>
                            {fisico.percentual >= 61 && (
                              <span className="text-sm text-red-600 font-medium">Atenção necessária</span>
                            )}
                          </div>
                          <p className="text-texto-suave text-sm mb-4">
                            Esses são os sinais que seu corpo está enviando e que precisam de cuidado:
                          </p>
                          <div className="w-full h-3 bg-bege-medio rounded-full overflow-hidden mb-2">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${fisico.percentual}%`, backgroundColor: corBarra(fisico.percentual) }}
                            />
                          </div>
                          <p className="text-xs text-texto-suave mb-4">
                            {fisico.simCount} de {fisico.total} sinais identificados
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {cardsFisicos.map(id => (
                              <div key={id} className="bg-verde-muito-claro rounded-lg p-3 text-sm text-texto-escuro">
                                {LABELS_CARDS_FISICOS[id]}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {cardsEmocionais.length > 0 && (
                        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-cormorant text-2xl font-semibold text-texto-escuro">Equilíbrio Emocional</h3>
                            {emocional.percentual >= 61 && (
                              <span className="text-sm text-red-600 font-medium">Atenção necessária</span>
                            )}
                          </div>
                          <p className="text-texto-suave text-sm mb-4">
                            Esses são os sentimentos identificados que estão pedindo atenção e transformação:
                          </p>
                          <div className="w-full h-3 bg-bege-medio rounded-full overflow-hidden mb-2">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${emocional.percentual}%`, backgroundColor: corBarra(emocional.percentual) }}
                            />
                          </div>
                          <p className="text-xs text-texto-suave mb-4">
                            {emocional.simCount} de {emocional.total} sinais identificados
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {cardsEmocionais.map(id => (
                              <div key={id} className="bg-verde-muito-claro rounded-lg p-3 text-sm text-texto-escuro">
                                {LABELS_CARDS_EMOCIONAIS[id]}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )
                })()}

                {exibirOleosResultadoCorporativo ? (
                  <>
                    <h3 className="font-cormorant text-2xl font-semibold text-texto-escuro mb-4">
                      De acordo com seus desconfortos físicos e emocionais, {oleosIndicados.length} óleos essenciais podem trazer mudanças reais para a sua vida:
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                      {oleosIndicados.map(oleo => {
                        const info = OLEOS_INFO[oleo]
                        return (
                          <div key={oleo} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                            <div className="text-4xl mb-3">{info?.emoji}</div>
                            <h3 className="font-semibold text-texto-escuro mb-2">{info?.nome || oleo}</h3>
                            <p className="text-sm text-texto-suave">{info?.beneficio}</p>
                          </div>
                        )
                      })}
                    </div>

                    <div className="bg-verde-muito-claro border-l-4 border-verde-escuro p-6 mb-8 rounded">
                      <p className="text-texto-escuro text-sm leading-relaxed">
                        Em breve entrarei em contato pelo WhatsApp para te apresentar uma devolutiva personalizada e te orientar sobre como usar os óleos essenciais no seu dia a dia.
                      </p>
                    </div>

                    <div className="bg-verde-muito-claro border-l-4 border-verde-escuro p-6 mb-6 rounded">
                      <h3 className="font-semibold text-texto-escuro mb-3 text-lg">💡 Você pode começar no seu ritmo</h3>
                      <p className="text-texto-suave text-sm leading-relaxed">
                        Cada óleo essencial é uma ferramenta poderosa e versátil, um único frasco pode atender inúmeros desconfortos do dia a dia. Há óleos relaxantes, que promovem um sono tranquilo e aliviam tensões, e óleos revigorantes, que trazem foco, otimismo e energia. Juntos, eles formam um suporte completo para o seu bem-estar físico e emocional, para aquele momento de acordar com tensão, a ansiedade do dia, o cansaço que não passa, o desânimo que aparece. Os óleos essenciais não substituem tratamentos médicos, mas podem ser uma ferramenta diária de cuidado, equilíbrio e transformação, para você e para quem você ama.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                      <h3 className="font-semibold text-texto-escuro mb-3 text-lg">💡 Potencialize ainda mais</h3>
                      <p className="text-texto-suave text-sm leading-relaxed mb-4">
                        Os óleos essenciais funcionam ainda melhor quando associados a terapias integrativas, meditação e práticas de autocuidado. Essa combinação potencializa os resultados e aprofunda o processo de equilíbrio físico e emocional. Experimente incluir aos poucos:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-texto-suave">
                        <li>Momentos de meditação ou respiração consciente</li>
                        <li>Práticas corporais como yoga ou alongamento</li>
                        <li>Terapias integrativas complementares</li>
                        <li>Pausas de autocuidado ao longo do dia</li>
                      </ul>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
                      <p className="text-blue-900 text-sm">
                        Onde consta "Mix", trata-se de uma combinação de óleos essenciais em um único frasco, formulada para atuar de forma mais completa e potente, cuidando do desconforto físico e emocional ao mesmo tempo. Cada Mix é desenvolvido com óleos que se complementam e potencializam seus efeitos.
                      </p>
                    </div>

                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-8 rounded">
                      <p className="text-yellow-900 text-sm">
                        Para casos mais específicos de saúde não apresentados neste diagnóstico, é necessário consultar cada situação de forma particular. Este diagnóstico existe para que você perceba que há soluções naturais para cada pessoa, e que o seu bem-estar pode ser cuidado de forma individualizada.
                      </p>
                    </div>

                    <BlocoApresentacaoConsultora consultora={consultora} modo="resultado" />
                  </>
                ) : (
                  <>
                    <div className="bg-verde-muito-claro border-l-4 border-verde-escuro p-6 mb-6 rounded">
                      <p className="text-texto-escuro text-sm leading-relaxed">
                        Em breve entrarei em contato pelo WhatsApp para apresentar uma devolutiva personalizada e orientações sobre como cuidar da sua saúde de forma natural.
                      </p>
                    </div>
                    <BlocoApresentacaoConsultora consultora={consultora} modo="identidade" />
                  </>
                )}
              </motion.div>
              )
            })()}

          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export { gerarSlugDe }
export default DiagnosticoCorporativo
